import { api } from './api';

export const orderApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getOrder: builder.mutation({
      query: ({
        order,
        orderBy,
        filter,
        filterKey,
        headerFilters,
        orderId,
        orderIndex,
        dateRange,
      }) => {
        return {
          url: 'getOrder',
          method: 'POST',
          body: {
            order,
            orderBy,
            filter,
            filterKey,
            headerFilters,
            orderId,
            orderIndex,
            dateRange,
          },
        };
      },
    }),

    checkoutOrders: builder.mutation({
      query: ({ checkoutAsUserId, itemOverrides, appliedCouponCodes }) => {
        return {
          url: 'checkoutOrders',
          method: 'POST',
          body: {
            checkoutAsUserId,
            itemOverrides,
            appliedCouponCodes,
          },
        };
      },
    }),

    getOrders: builder.query({
      query: ({
        order,
        orderBy,
        filter,
        filterKey,
        headerFilters,
        pageIndex,
        pageSize,
        userId,
        dateRange,
      }) => {
        return {
          url: 'getOrders',
          method: 'POST',
          body: {
            order,
            orderBy,
            filter,
            filterKey,
            headerFilters,
            page: pageIndex,
            pageSize,
            userId,
            dateRange,
          },
        };
      },
    }),

    getOrderReminders: builder.query({
      query: ({
        filter,
        filterKey,
        headerFilters,
        shippingReminderLimit,
        shippingReminderDateFrom,
        shippingReminderDateTo,
        pageIndex = 0,
        pageSize,
        userId,
        excludeReceived = true,
      }) => {
        const limit = (typeof shippingReminderLimit === 'number')
          ? shippingReminderLimit
          : (typeof pageSize === 'number' ? pageSize : 10);
        const resolvedPageSize = (typeof pageSize === 'number')
          ? pageSize
          : limit;
        return {
          url: 'getOrderReminders',
          method: 'POST',
          body: {
            filter,
            filterKey,
            headerFilters,
            page: pageIndex,
            pageSize: resolvedPageSize,
            userId,
            shippingReminderLimit: limit,
            shippingReminderDateFrom,
            shippingReminderDateTo,
            excludeReceived,
          },
        };
      },
    }),

    updateOrder: builder.mutation({
      query: ({ orderId, userId, diffs }) => {
        return {
          url: 'updateOrder',
          method: 'POST',
          body: {
            orderId,
            userId,
            diffs,
          },
        };
      },
    }),

    updatePendingItem: builder.mutation({
      query: ({
        orderId, itemId, shipDate, deadline,
      }) => {
        return {
          url: 'updatePendingItem',
          method: 'POST',
          body: {
            orderId,
            itemId,
            shipDate,
            deadline,
          },
        };
      },
    }),

    createBulkOrder: builder.mutation({
      query: ({ orderIds }) => {
        return {
          url: 'createBulkOrder',
          method: 'POST',
          body: {
            orderIds,
          },
        };
      },
    }),

    getBulkOrder: builder.mutation({
      query: ({
        order,
        orderBy,
        filter,
        filterKey,
        headerFilters,
        bulkOrderId,
        bulkOrderIndex,
        dateRange,
      }) => {
        return {
          url: 'getBulkOrder',
          method: 'POST',
          body: {
            order,
            orderBy,
            filter,
            filterKey,
            headerFilters,
            bulkOrderId,
            bulkOrderIndex,
            dateRange,
          },
        };
      },
    }),

    getBulkOrders: builder.query({
      query: ({
        order,
        orderBy,
        filter,
        filterKey,
        headerFilters,
        pageIndex,
        pageSize,
        dateRange,
      }) => {
        return {
          url: 'getBulkOrders',
          method: 'POST',
          body: {
            order,
            orderBy,
            filter,
            filterKey,
            headerFilters,
            page: pageIndex,
            pageSize,
            dateRange,
          },
        };
      },
    }),

    updateBulkOrder: builder.mutation({
      query: ({ bulkOrderId, diffs }) => {
        return {
          url: 'updateBulkOrder',
          method: 'POST',
          body: {
            bulkOrderId,
            diffs,
          },
        };
      },
    }),

    receiveAllBarcodeItems: builder.mutation({
      query: ({ bulkOrderId, inventoryOrderId, barcodes }) => {
        return {
          url: 'receiveAllBarcodeItems',
          method: 'POST',
          body: {
            inventoryOrderId,
            bulkOrderId,
            barcodes,
          },
        };
      },
    }),

    getSalespersonReport: builder.mutation({
      query: ({
        salespersonId,
        dateFrom,
        dateTo,
      }) => {
        return {
          url: 'getSalespersonReport',
          method: 'POST',
          body: {
            salespersonId,
            dateFrom,
            dateTo,
          },
        };
      },
    }),

    getCustomerReport: builder.mutation({
      query: ({
        customerId,
        dateFrom,
        dateTo,
      }) => {
        return {
          url: 'getCustomerReport',
          method: 'POST',
          body: {
            customerId,
            dateFrom,
            dateTo,
          },
        };
      },
    }),

    // Reminders
    createReminder: builder.mutation({
      query: ({
        orderId,
        name,
        description,
        criticalLevel,
        reminderDate,
      }) => {
        return {
          url: 'createReminder',
          method: 'POST',
          body: {
            orderId,
            name,
            description,
            criticalLevel,
            reminderDate,
          },
        };
      },
      invalidatesTags: ['Reminders'],
    }),

    deleteReminder: builder.mutation({
      query: ({ reminderId }) => {
        return {
          url: 'deleteReminder',
          method: 'POST',
          body: {
            reminderId,
          },
        };
      },
      invalidatesTags: ['Reminders'],
    }),

    getReminders: builder.query({
      query: ({
        orderId,
        criticalLevel,
        dateFrom,
        dateTo,
        limit,
        offset,
      }) => {
        const params = new URLSearchParams();
        if (orderId) params.append('orderId', orderId);
        if (criticalLevel) params.append('criticalLevel', criticalLevel);
        if (dateFrom) params.append('dateFrom', dateFrom);
        if (dateTo) params.append('dateTo', dateTo);
        if (limit) params.append('limit', limit);
        if (offset) params.append('offset', offset);

        return {
          url: `getReminders?${params.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Reminders'],
    }),

    getRemindersSummary: builder.query({
      query: () => {
        return {
          url: 'getRemindersSummary',
          method: 'GET',
        };
      },
      providesTags: ['Reminders'],
    }),

  }),
});

export const {
  useGetOrderMutation,
  useCheckoutOrdersMutation,
  useGetOrdersQuery,
  useGetOrderRemindersQuery,
  useUpdateOrderMutation,
  useUpdatePendingItemMutation,

  useGetBulkOrderMutation,
  useCreateBulkOrderMutation,
  useGetBulkOrdersQuery,
  useUpdateBulkOrderMutation,
  useReceiveAllBarcodeItemsMutation,

  useGetSalespersonReportMutation,
  useGetCustomerReportMutation,

  useCreateReminderMutation,
  useDeleteReminderMutation,
  useGetRemindersQuery,
  useGetRemindersSummaryQuery,
} = orderApi;
